import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import {
  LoginRequest,
  RegistrationRequest,
  UpdateUserDTO,
} from "../types/userDTOs";

/** Session-token key. Web stores it in sessionStorage, mobile in the OS keychain. */
export const JWT_NAME = "jwt-token-inkhunt";

export interface ApiUrls {
  REGISTER: string;
  LOGIN: string;
  GOOGLE_LOGIN: string;
  VERIFY_REGISTRATION: string;
  ART_STYLES: string;
  ADMIN_CONSOLE: string;
  USERS: string;
  POSTS: string;
  SEARCH: string;
}

export const buildApiUrls = (backendUrl: string): ApiUrls => {
  const base = `${backendUrl}/api/v1`;
  return {
    REGISTER: `${base}/register`,
    LOGIN: `${base}/login`,
    GOOGLE_LOGIN: `${base}/login/google`,
    VERIFY_REGISTRATION: `${base}/verify-registration`,
    ART_STYLES: `${base}/art-styles`,
    ADMIN_CONSOLE: `${base}/admin-console`,
    USERS: `${base}/users`,
    POSTS: `${base}/posts`,
    SEARCH: `${base}/search`,
  };
};

/**
 * Everything platform-specific the client needs. The web supplies sessionStorage and
 * `window.location`; React Native supplies expo-secure-store and its router. Keeping these
 * injected is what lets the API layer be shared — previously the interceptors reached for
 * `sessionStorage` and `window.location` directly, which are the only browser APIs the whole
 * data layer ever touched.
 */
export interface ApiClientOptions {
  backendUrl: string;
  /** May be async: the mobile keychain read returns a Promise. */
  getToken: () => string | null | Promise<string | null>;
  clearToken: () => void | Promise<void>;
  /**
   * Called after a 401 has cleared the session. Receives the `www-authenticate` header value
   * as a reason, which the web forwards to /login as a query param. Navigation lives here
   * rather than inside the interceptor so each platform routes its own way.
   */
  onUnauthorized: (reason: string) => void;
}

/**
 * Builds a configured axios instance. Pure — takes its platform dependencies as arguments and
 * touches no globals, so it can be unit-tested with a mock adapter.
 */
export const createApiClient = (options: ApiClientOptions): AxiosInstance => {
  const client = axios.create();

  // Attach the bearer token when we have one (login/register have none yet). Axios allows an
  // async request interceptor, which is what lets the mobile keychain read work here too.
  client.interceptors.request.use(async (request) => {
    const token = await options.getToken();
    if (token) {
      request.headers.Authorization = `Bearer ${token}`;
    }
    return request;
  });

  // Any 401 means the session is gone — clear it and hand off to the platform's navigation.
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response?.status === 401) {
        await options.clearToken();
        const reason = error.response.headers?.["www-authenticate"] ?? "";
        options.onUnauthorized(reason);
      }
      return Promise.reject(error);
    }
  );

  return client;
};

// The app-wide instance. Each platform calls configureApi() once at startup; the api call
// modules below read it lazily, so import order between them doesn't matter.
let instance: AxiosInstance | null = null;
let urls: ApiUrls | null = null;
let configured: ApiClientOptions | null = null;

export const configureApi = (options: ApiClientOptions): AxiosInstance => {
  instance = createApiClient(options);
  urls = buildApiUrls(options.backendUrl);
  configured = options;
  return instance;
};

const notConfigured = (what: string) =>
  new Error(
    `@inkhunt/core: ${what} used before configureApi() was called. ` +
      "Call configureApi() once during app startup."
  );

export const getApiClient = (): AxiosInstance => {
  if (!instance) throw notConfigured("API client");
  return instance;
};

export const API_URLS = (): ApiUrls => {
  if (!urls) throw notConfigured("API URLs");
  return urls;
};

/**
 * Reads the stored session token through the platform adapter. Hooks that need to know
 * whether a session exists go through this rather than touching storage themselves.
 */
export const getSessionToken = (): string | null | Promise<string | null> => {
  if (!configured) throw notConfigured("session token");
  return configured.getToken();
};

/** Test seam: drops the configured instance so each test starts clean. */
export const resetApi = () => {
  instance = null;
  urls = null;
  configured = null;
};

/**
 * A file being uploaded. The web passes a `File`; React Native has no `File` and passes the
 * `{ uri, name, type }` shape its FormData understands.
 */
export type UploadFile = File | { uri: string; name: string; type: string };

/** FormData types only accept Blob/string, so the RN descriptor needs a cast to append. */
export const appendFile = (form: FormData, field: string, file: UploadFile) =>
  form.append(field, file as unknown as Blob);

// Query-param carriers. The auth header is injected by the request interceptor above.
export const config = (params?: Record<string, unknown>): AxiosRequestConfig => ({
  params,
});

export const configNoAuth = (
  params?: Record<string, unknown>
): AxiosRequestConfig => ({
  params,
});

export const fetch = <T>(url: string, config?: AxiosRequestConfig<T>) =>
  getApiClient().get<T>(url, config);

export const create = <T>(
  url: string,
  data?: LoginRequest | RegistrationRequest,
  config?: AxiosRequestConfig<T>
) => getApiClient().post<T>(url, data, config);

export const update = <T>(
  url: string,
  data?: UpdateUserDTO,
  config?: AxiosRequestConfig<T>
) => getApiClient().put<T>(url, data, config);

export const remove = <T>(url: string, config?: AxiosRequestConfig<T>) =>
  getApiClient().delete<T>(url, config);
